// @ts-nocheck
// TypeScript is ignoring this file. If you are editing this file you must fix the type issues.

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LiliButton } from '@lili/lili-frontend-common';
import BackIcon from '../../../assets/svg/backArrow.svg';
import ContentWrapper from '../../../generic-components/ContentWrapper';
import MainContent from '../../../generic-components/Layout/MainContent';
import UpperMessagePopUp from '../../transactions/components/UpperMessagePopUp';
import moneyMovementIcon from '../../../assets/icons/svg/colored-icons/ic-colored-success.svg';
import Icon from '../../../generic-components/Icon';
import {
  cardOrdered,
  cardStolenDamagedLost,
  changesUnavailableIcon,
  needFundsIcon,
  orderNewCardConfirmAddress,
} from '../../../assets/svg';
import {
  DAYS_FROM_OPEN_ACCOUNT_IN_MS,
  ERROR_MODAL_TEXTS,
  NEW_CARD_ORDER_REASONS,
  ORDER_NEW_CARD_FLOWS,
  UI_TYPE,
  LILI_CARD_STYLES,
} from '../constants';
import Button from '../../../generic-components/Button';
import AddressArea from '../components/AddressArea';
import { useStore } from '../../../store';
import { POPUP_LIFESPAN } from '../../emergencyBucketDashboard/constants';
import { useUpdateAddress } from '../../../hooks/query/useUpdateAddress';
import { useAccountSummaryQuery } from '../../../hooks/query/useAccountSummaryQuery';
import InfoPopup from '../../dashboard/components/InfoPopup';
import { useOrderNewCardQuery } from '../../../hooks/query/useOrderNewCardQuery';
import { IAddressValidationObject, IServerError } from '../../../types';
import somethingWentWrong from '../../../assets/svg/something-went-wrong.svg';
import { LOGIN_ERROR_POPUP } from '../../login/constants';
import { getMostRelevantCard } from '../../../helpers/cardStatus.helper';
import { shouldDisableCardReissue } from '../helpers/orderNewCard.helper';
import Heading from '../../../generic-components/Heading';
import { Layout } from '../../../components/Layout/Layout';

const OrderNewCard = () => {
  const { t } = useTranslation();
  const summary = useStore((state) => state.summary);
  const { getPrimaryAccount, getPersonalAccount } = useStore((state) => state);
  const balanceValue = getPrimaryAccount().balance;
  const cardObject = getMostRelevantCard(getPersonalAccount().cards);
  const { customerId } = useStore((state) => state.user);
  const HEADER_STOLEN_LOST = 'ORDER_NEW_CARD_STEP1_TITLE_STOLEN_LOST';
  const HEADER_DAMAGED = 'ORDER_NEW_CARD_STEP1_TITLE_DAMAGED';
  const ORDERED_LOST_OR_STOLEN_CARD = 'ORDER_NEW_CARD_ORDERED_LOST_OR_STOLEN_CARD';
  const ORDERED_DAMAGED_CARD = 'ORDER_NEW_CARD_ORDERED_DAMAGED_CARD';
  const { FORM, TEXT_AREA } = UI_TYPE;
  const userAddress = summary?.customer?.address;
  const navigate = useNavigate();

  const canUserChangeAddress = () => {
    const createTime = summary?.customer?.createTime;
    if (createTime) {
      const userCreationTimeInMS = new Date(createTime).getTime();
      const timeNow = Date.now();
      return timeNow - userCreationTimeInMS > DAYS_FROM_OPEN_ACCOUNT_IN_MS;
    }
    return false;
  };

  const INITIAL_STATE = {
    street_address: userAddress?.line1 ?? '',
    apt_suite: userAddress?.line2 ?? '',
    city: userAddress?.city ?? '',
    state: userAddress?.state ?? '',
    zip_code: userAddress?.postalCode ?? '',
  };

  const ERROR_POPUP_DATA_INITIAL_STATE = {
    title: '',
    subtitle: '',
    buttonsQuantity: 0,
  };

  const [addressObject, setAddressObject] = useState(INITIAL_STATE);
  const [step, setStep] = useState<number>(0);
  const [currentFlow, setCurrentFlow] = useState<string>();
  const [showUpperMessagePopUp, setShowUpperMessagePopUp] = useState<boolean>(false);
  const [showInfoPopup, setShowInfoPopup] = useState<boolean>(false);
  const [showErrorOrderNewCardPopup, setShowErrorOrderNewCardPopup] = useState<boolean>(false);
  const [orderReason, setOrderReason] = useState<string>('');
  const [addressAreaType, setAddressAreaType] = useState(TEXT_AREA);
  const [showChangesUnavailable, setShowChangesUnavailable] = useState(false);
  const [thereIsErrorPopupShown, setThereIsErrorPopupShown] = useState(false);
  const [errorPopupData, setErrorPopupData] = useState(ERROR_POPUP_DATA_INITIAL_STATE);

  const INITIAL_VALIDATION_STATE = {
    street_address: addressObject.street_address !== '',
    apt_suite: addressObject.apt_suite.length < 29,
    city: addressObject.city !== '',
    state: addressObject.state !== '',
    zip_code: addressObject.zip_code !== '',
    hasNoServerErrors: true,
  };
  const [validationObject, setValidationObject] = useState<IAddressValidationObject>(INITIAL_VALIDATION_STATE);

  const { F_DAMAGED, F_STOLEN_LOST } = ORDER_NEW_CARD_FLOWS;
  const { R_STOLEN, R_LOST, R_DAMAGED } = NEW_CARD_ORDER_REASONS;

  const isAddressChanged = () =>
    !(
      userAddress?.line1 === addressObject.street_address &&
      userAddress?.line2 === addressObject.apt_suite &&
      userAddress?.city === addressObject.city &&
      userAddress?.state === addressObject.state &&
      userAddress?.postalCode === addressObject.zip_code
    );
  const closePopup = () => {
    setShowUpperMessagePopUp(false);
  };

  const { refetch: updateSummary } = useAccountSummaryQuery(() => {
    if (addressAreaType === FORM) {
      setAddressAreaType(TEXT_AREA);
      setShowUpperMessagePopUp(true);
      setTimeout(closePopup, POPUP_LIFESPAN);
    }
  });

  const shouldDisableCardDamagedButton = cardObject !== null && shouldDisableCardReissue(cardObject);
  const disabledCardButtonClassName = shouldDisableCardDamagedButton
    ? LILI_CARD_STYLES.DISABLED_ORDER_REASON_BUTTON
    : '';

  const handleTryAgain = () => {
    setValidationObject({ ...validationObject, hasNoServerErrors: true });
  };

  const handleLater = () => {
    setValidationObject(INITIAL_VALIDATION_STATE);
    setAddressObject(INITIAL_STATE);
    setAddressAreaType(TEXT_AREA);
  };

  const handleErrorOrderNewCard = (errorObject: IServerError) => {
    const error = errorObject?.response?.data?.errors?.[0];
    const errorCode = error?.code;
    if (errorCode === 5027) {
      setShowErrorOrderNewCardPopup(true);
    } else {
      setShowInfoPopup(false);
      setThereIsErrorPopupShown(true);
    }
  };

  const handleErrorUpdateAddress = (errorObject: IServerError) => {
    const error = errorObject?.response?.data?.errors?.[0];
    const errorMessage = error?.localizedMessage;
    if (error && error.code) {
      const errorCode = error.code;
      switch (errorCode) {
        case 4052:
          setValidationObject({ ...validationObject, street_address: false });
          break;
        case 4043:
          setValidationObject({ ...validationObject, city: false });
          break;
        case 4045:
          setValidationObject({ ...validationObject, state: false });
          break;
        case 4046:
          setValidationObject({ ...validationObject, zip_code: false });
          break;
        case 40067:
          setErrorPopupData({
            title: t('CHANGE_ADDRESS_FORM_SERVER_ERROR_40067_TITLE'),
            subtitle: t('CHANGE_ADDRESS_FORM_SERVER_ERROR_40067_SUBTITLE'),
            buttonsQuantity: 2,
          });
          setValidationObject({ ...validationObject, hasNoServerErrors: false });
          break;
        case 40066:
          setErrorPopupData({
            ...errorPopupData,
            title: t('CHANGE_ADDRESS_FORM_SERVER_ERROR_40066_TITLE'),
            buttonsQuantity: 2,
          });
          setValidationObject({ ...validationObject, hasNoServerErrors: false });
          break;
        case 4413:
          setErrorPopupData({ ...errorPopupData, title: errorMessage ?? '', buttonsQuantity: 1 });
          setValidationObject({ ...validationObject, hasNoServerErrors: false });
          break;
        default:
          setErrorPopupData({ ...errorPopupData, title: errorMessage ?? '', buttonsQuantity: 2 });
          setValidationObject({ ...validationObject, hasNoServerErrors: false });
      }
    } else {
      setErrorPopupData({ ...errorPopupData, title: errorMessage ?? '', buttonsQuantity: 2 });
      setValidationObject({ ...validationObject, hasNoServerErrors: false });
    }
  };

  const { refetch: updateAddress, isFetching: isAddressUpdating } = useUpdateAddress({
    customerId,
    dataObj: addressObject,
    onSuccess: updateSummary,
    onError: handleErrorUpdateAddress,
  });
  const toggleAddressAreaType = async () => {
    if (addressAreaType === FORM) {
      await updateAddress().then(() => { });
    }
    if (addressAreaType === TEXT_AREA) {
      if (canUserChangeAddress()) {
        setAddressAreaType(FORM);
      } else {
        setShowChangesUnavailable(true);
      }
    }
  };

  const { refetch: orderNewCard, isLoading: isOrdering } = useOrderNewCardQuery({
    customerId,
    cardId: cardObject?.cardId || '',
    reason: orderReason,
    onSuccess: () => {
      setShowInfoPopup(false);
      setStep(step + 1);
    },
    onError: handleErrorOrderNewCard,
  });

  useEffect(() => {
    setValidationObject(INITIAL_VALIDATION_STATE);
  }, [addressObject]);

  return (
    <Layout wrapperClassName='pt-[78px] pl-[72px] pr-[32px] ml-[100px] pc-md:ml-[256px]'>
      <div className='flex items-center gap-[24px] mb-[24px]'>
        <Button
          onClick={() => {
            if (step === 0) {
              navigate('/my-lili-card');
            } else if (step === 1) {
              setAddressObject(INITIAL_STATE);
              setAddressAreaType(TEXT_AREA);
              setStep(step - 1);
            } else if (step === 2) {
              updateSummary();
              navigate('/dashboard');
            } else {
              setStep(step - 1);
            }
          }}
          className=''
          id='backActivateCard'
          childElement={<Icon className='w-[10px]' src={BackIcon} />}
        />
        <Heading title={t('ORDER_NEW_CARD_PAGE_HEADER')} className='font-metricRSemibold text-bw08 text-[20px]' />
      </div>
      {showUpperMessagePopUp && (
        <UpperMessagePopUp
          iconSource={moneyMovementIcon}
          handleClose={closePopup}
          messageText={t('ORDER_NEW_CARD_UPPER_MESSAGE')}
        />
      )}
      <ContentWrapper>
        <MainContent className='flex flex-col gap-4'>
          <div
            className={`flex flex-col bg-bw00 rounded-xl w-[456px] ${step === 1 ? 'h-[594px]' : 'h-[548px]'} p-[40px]`}
          >
            {step === 0 && (
              <>
                <Icon className='w-[62px] h-[56px] mb-[36px]' src={cardStolenDamagedLost} />
                <span className='h-[40px] font-metricMedium text-bw08 text-[25px] mb-[0.5rem]'>
                  {t('ORDER_NEW_CARD_STEP0_TITLE')}
                </span>
                <span className='h-[24px] text-bw07 font-metric text-base mb-[40px]'>
                  {t('ORDER_NEW_CARD_STEP0_TEXT')}
                </span>
                <div className='w-full flex flex-col items-center gap-2 mb-[54px]'>
                  <Button
                    className={`w-[200px] h-[44px] border ${orderReason === R_STOLEN ? 'border-action03' : 'border-bw03'} hover:bg-bw02 cursor-pointer rounded-umd font-metricMedium text-sm text-bw08`}
                    onClick={() => {
                      setOrderReason(R_STOLEN);
                    }}
                    title={t('ORDER_NEW_CARD_STOLEN')}
                    id='MyCardWasStolen'
                  />
                  <Button
                    className={`w-[200px] h-[44px] border ${orderReason === R_LOST ? 'border-action03' : 'border-bw03'} hover:bg-bw02 cursor-pointer rounded-umd font-metricMedium text-sm text-bw08`}
                    onClick={() => {
                      setOrderReason(R_LOST);
                    }}
                    title={t('ORDER_NEW_CARD_LOST')}
                    id='MyCardWasLost'
                  />
                  <Button
                    disable={shouldDisableCardDamagedButton}
                    className={`w-[200px] h-[44px] border ${orderReason === R_DAMAGED ? 'border-action03' : 'border-bw03'} hover:bg-bw02 cursor-pointer rounded-umd font-metricMedium text-sm text-bw08 ${disabledCardButtonClassName}`}
                    onClick={() => {
                      setOrderReason(R_DAMAGED);
                    }}
                    title={t('ORDER_NEW_CARD_DAMAGED')}
                    id='MyCardWasDamaged'
                  />
                </div>
                <LiliButton
                  onClick={() => {
                    if (orderReason) {
                      if (orderReason === R_STOLEN || orderReason === R_LOST) {
                        setCurrentFlow(F_STOLEN_LOST);
                      } else setCurrentFlow(F_DAMAGED);
                      setStep(step + 1);
                    }
                  }}
                  className='w-[200px] h-[50px] self-center'
                  title={t('TAX_BUCKET_PERSONALIZATION_CONTINUE_BUTTON_TEXT')}
                  id='continueOrderNewCard'
                />
              </>
            )}
            {step === 1
              && (
                <>
                  {/* server error popup from order new card */}
                  {showErrorOrderNewCardPopup && (
                    <InfoPopup
                      handleState={() => { }}
                      additionalCallback={() => {
                        navigate('/ach-transfer');
                      }}
                      pictureSrc={needFundsIcon}
                      pictureClassName='w-[74px] h-[70px] mb-[24px]'
                      showInfoPopup={false}
                      flexParams='flex flex-col justify-start items-center'
                      className='px-[40px] pt-[71px]'
                      popupHeight='h-[486px]'
                      text=''
                      multiFormatTexts={(
                        <>
                          <span className='whitespace-pre-line text-base text-center font-metric text-bw07 leading-[27px]'>
                            {t('ORDER_NEW_CARD_ERROR_POPUP_TEXT1_LINE1_1')}
                            {balanceValue}
                            {t('ORDER_NEW_CARD_ERROR_POPUP_TEXT1_LINE1_2')}
                          </span>
                          <span className='whitespace-pre-line text-base text-center font-metric text-bw07 leading-[27px]'>
                            {t('ORDER_NEW_CARD_ERROR_POPUP_TEXT1_LINE2_1')}
                            <span className='font-metricMedium text-bw08'>{t('ORDER_NEW_CARD_ERROR_POPUP_TEXT1_LINE2_2')}</span>
                            {t('ORDER_NEW_CARD_ERROR_POPUP_TEXT1_LINE2_3')}
                          </span>
                        </>
                      )}
                      text2={t('ORDER_NEW_CARD_ERROR_POPUP_TEXT2')}
                      text2ClassName='text-base text-center font-metric text-bw07 whitespace-pre-line mt-[12px] leading-[27px] mb-[45px]'
                      title={t('ORDER_NEW_CARD_ERROR_POPUP_TITLE')}
                      titleClassName='text-center whitespace-pre-line text-base font-metricMedium text-bw08 mb-[1rem]'
                      mainButtonText={t('ORDER_NEW_CARD_ERROR_POPUP_BUTTON_TEXT')}
                      buttonClassName='w-[240px] h-[50px]'
                    />
                  )}
                  {/* server error popup from update address */}
                  {addressAreaType === FORM && !validationObject.hasNoServerErrors && (
                    <InfoPopup
                      className='w-[400px] h-[460px] pt-[60px] pb-[24px] px-0'
                      flexParams='flex flex-col justify-start items-center'
                      pictureClassName='w-[84px] h-[84px] mb-[24px]'
                      handleState={() => { }}
                      additionalCallback={() => {
                        if (errorPopupData.buttonsQuantity === 2) {
                          handleTryAgain();
                        } else handleLater();
                      }}
                      secondaryHandleState={() => { if (errorPopupData.buttonsQuantity === 2) handleLater(); }}
                      pictureSrc={changesUnavailableIcon}
                      showInfoPopup={false}
                      text={errorPopupData.subtitle}
                      textClassName='w-[332px] h-[110px] px-[34] leading-[22px] whitespace-pre-line mb-[43px]'
                      title={errorPopupData.title}
                      titleClassName='h-[24px] mb-[6px] text-center whitespace-pre-line'
                      mainButtonText={errorPopupData.buttonsQuantity === 1 ? t('OK') : t('LOGIN_ERROR_POPUP_BUTTON_TEXT')}
                      secondaryButtonText={errorPopupData.buttonsQuantity === 2 ? t('CHANGE_ADDRESS_FORM_SERVER_ERROR_BUTTON_TITLE') : undefined}
                      secondaryButtonClassName='text-sm text-bw06 font-metricMedium hover:text-action03 mt-[1rem]'
                    />
                  )}
                  {showChangesUnavailable && (
                    <InfoPopup
                      className='w-[400px] h-[460px] px-[34px]'
                      flexParams='flex flex-col justify-start items-center'
                      handleState={() => { setShowChangesUnavailable(false); }}
                      pictureSrc={changesUnavailableIcon}
                      pictureClassName='w-[84px] h-[84px] mt-[60px] mb-[24px]'
                      showInfoPopup={showChangesUnavailable}
                      text={t('CHANGES_UNAVAILABLE_TEXT')}
                      textClassName='whitespace-pre-line h-[66px] mb-[109px]'
                      title={t('CHANGES_UNAVAILABLE_TITLE')}
                      titleClassName='mb-[6px]'
                      mainButtonText={t('OK')}
                    />
                  )}
                  <Icon className='w-[64px] h-[45px] mb-[30px]' src={orderNewCardConfirmAddress} />
                  <span className='h-[84px] text-[24px] inline-flex items-center font-metricMedium text-bw08 mb-[0.5rem]'>{t(currentFlow === F_STOLEN_LOST ? HEADER_STOLEN_LOST : HEADER_DAMAGED)}</span>
                  <span className='text-base font-metric text-bw07 mb-[9px]'>{t('ORDER_NEW_CARD_CONFIRM_ADDRESS')}</span>
                  <AddressArea isLoading={isAddressUpdating} stateObject={{ addressObject, setAddressObject }} addressAreaType={addressAreaType} toggler={addressAreaType === TEXT_AREA || !isAddressChanged() ? toggleAddressAreaType : updateAddress} validationStateObject={{ validationObject, setValidationObject }} />
                  {addressAreaType === TEXT_AREA && <Button onClick={() => { setShowInfoPopup(true); }} title='Confirm Address' className='w-[200px] h-[50px] bg-action03 hover:bg-action04 self-end flex justify-center items-center rounded-umd text-bw01 text-sm font-metricSemibold mt-[56px]' id='confirmAddress' />}
                </>
              )}
            {step === 2 && (
              <>
                <Icon className='w-[74px] h-[70px] mb-[36px]' src={cardOrdered} />
                <span className='h-[40px] mb-[0.5rem] text-bw08 text-[25px] font-metricMedium inline-flex items-center'>
                  {t('ORDER_NEW_CARD_DONE_AND_DONE')}
                </span>
                <span className='h-[81px] pr-[84px] text-bw07 text-base font-metric leading-[168%] mb-[185px]'>
                  {t(orderReason === R_DAMAGED ? ORDERED_DAMAGED_CARD : ORDERED_LOST_OR_STOLEN_CARD)}
                </span>
                <Button
                  onClick={() => {
                    updateSummary();
                    navigate('/dashboard');
                  }}
                  className='w-[200px] h-[50px] bg-action03 hover:bg-action04 font-metricSemibold text-sm text-bw00 self-center'
                  title={t('ORDER_NEW_CARD_GREAT')}
                  id='GoToLiliCard'
                />
              </>
            )}
          </div>
          {showInfoPopup && (
            <InfoPopup
              additionalCallback={() => {
                orderNewCard();
              }}
              isLoading={isOrdering}
              popupHeight='h-[486px] px-[40px]'
              titleClassName='h-[50px] px-[50px] text-center text-base font-metricMedium text-bw08 mb-[1rem]'
              textClassName='h-[54px] mb-[53px]'
              pictureClassName='w-[62px] h-[56px] mt-[92px] mb-[45px]'
              mainButtonText={t('ORDER_NEW_CARD_POPUP_YES')}
              handleState={() => {}}
              buttonClassName='w-[240px] h-[50px]'
              secondaryHandleState={() => {
                setShowInfoPopup(false);
              }}
              secondaryButtonText={t('ORDER_NEW_CARD_POPUP_NO')}
              pictureSrc={cardStolenDamagedLost}
              showInfoPopup
              text={t('ORDER_NEW_CARD_POPUP_TEXT')}
              title={t('ORDER_NEW_CARD_POPUP_TITLE')}
            />
          )}
          {thereIsErrorPopupShown && (
            <InfoPopup
              popupHeight='h-[485px]'
              handleState={() => {}}
              pictureClassName='w-[122px] h-[108px] mt-5 mb-[40px]'
              pictureSrc={somethingWentWrong}
              showInfoPopup={thereIsErrorPopupShown}
              text={t(LOGIN_ERROR_POPUP.ERROR_TEXT)}
              textClassName='px-[34px] whitespace-pre-line mb-8'
              title={t(LOGIN_ERROR_POPUP.HEADER)}
              titleClassName='text-center px-20 text-base font-metricMedium text-bw08 mb-[1rem]'
              buttonClassName='min-h-[50px] w-[240px]'
              mainButtonText={t(ERROR_MODAL_TEXTS.BTN)}
              additionalCallback={() => setThereIsErrorPopupShown(false)}
            />
          )}
          {showUpperMessagePopUp && (
            <UpperMessagePopUp
              iconSource={moneyMovementIcon}
              handleClose={closePopup}
              messageText={t('ORDER_NEW_CARD_UPPER_MESSAGE')}
            />
          )}
        </MainContent>
      </ContentWrapper>
    </Layout>
  );
};

export default OrderNewCard;
